import { CaptureRejection, DEFAULT_RESIZE, ResizeOptions } from '../model';

/**
 * Scale so the longest edge is at most `maxEdgePx`, preserving aspect ratio and only ever
 * downscaling (an image already within bounds is returned unchanged). Ported from the prod
 * cards resize logic; extracted as a pure function so it is unit-testable without a canvas.
 */
export function computeScaledDimensions(
  width: number,
  height: number,
  maxEdgePx: number
): { width: number; height: number } {
  const longestEdge = Math.max(width, height);
  const scale = longestEdge > maxEdgePx ? maxEdgePx / longestEdge : 1;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/**
 * True if `fileType`/`fileName` satisfies an `accept` string (comma-separated list of MIME
 * types, `type/*` wildcards, or `.ext` extensions).
 */
export function matchesAccept(fileType: string, fileName: string, accept: string): boolean {
  const tokens = accept
    .split(',')
    .map(token => token.trim().toLowerCase())
    .filter(token => token.length > 0);
  if (tokens.length === 0) {
    return true;
  }
  const type = fileType.toLowerCase();
  const name = fileName.toLowerCase();
  return tokens.some(token => {
    if (token.startsWith('.')) {
      return name.endsWith(token);
    }
    if (token.endsWith('/*')) {
      return type.startsWith(token.slice(0, -1));
    }
    return type === token;
  });
}

/**
 * Pre-resize guard: rejects a file whose type is not accepted, or that exceeds `maxBytes`.
 * Type is checked before size, matching the prod cards `checkFileTypeAndSize`.
 */
export function checkFile(file: File, options: { accept: string; maxBytes: number }): CaptureRejection | null {
  if (!matchesAccept(file.type, file.name, options.accept)) {
    return { fileName: file.name, reason: 'file-type' };
  }
  if (file.size > options.maxBytes) {
    return { fileName: file.name, reason: 'file-too-large' };
  }
  return null;
}

/**
 * Decode a picked image file, downscale it to the given bounds, and return a JPEG data URL.
 * Uses `createImageBitmap` + canvas (the modern, zoneless-friendly path). The scaling maths is
 * the faithful port of the prod cards behaviour (see {@link computeScaledDimensions}).
 */
export async function resizeImage(image: Blob, options: ResizeOptions = DEFAULT_RESIZE): Promise<string> {
  const bitmap = await createImageBitmap(image);
  try {
    const { width, height } = computeScaledDimensions(bitmap.width, bitmap.height, options.maxEdgePx);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (context === null) {
      throw new Error('canvas 2d context unavailable');
    }
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(bitmap, 0, 0, width, height);
    return canvas.toDataURL('image/jpeg', options.quality);
  } finally {
    bitmap.close();
  }
}
